import React, { useState } from "react";
import {
  AiOutlineClose,
  AiOutlineCalendar,
  AiOutlineUser,
} from "react-icons/ai";
import { FiClock } from "react-icons/fi";

import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";

import useOutsideClick from "hooks/click-outside";
import useAmbassador from "containers/ambassador/useHooks";
import { confirmationPopup } from "utils/helpers/common/alert-service";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  eventData?: any;
};

const EventDetailModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  eventData,
}) => {
  const { joinAmbassadorEvent } = useAmbassador();
  const modalRef = useOutsideClick(onClose);

  if (!isOpen || !eventData) return null;

  // Helper functions
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleJoiningEvent = async () => {
    const result = await confirmationPopup(`Are you sure you want to join?`);
    if (result.isConfirmed) {
      // Handle joining event logic here
      joinAmbassadorEvent(eventData.id, {});
    }
  };

  const imageUrl =
    eventData.media && eventData.media.length > 0
      ? concatPathWithBackendUrl(eventData.media[0].name)
      : "";
  const subsCount = eventData.subscriptions?.length || 0;

  return (
    <div
      className={`fixed z-50 inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        ref={modalRef}
        className={`relative bg-white rounded-lg shadow-lg w-[90%] max-w-2xl max-h-[90vh] overflow-y-auto transform transition-transform duration-300 ${
          isOpen ? "scale-100" : "scale-95"
        }`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition z-10"
        >
          <AiOutlineClose size={24} />
        </button>

        {/* Event Image */}
        {imageUrl && (
          <div className="relative h-48 w-full">
            <img
              src={imageUrl}
              alt={eventData.name}
              className="w-full h-full object-cover rounded-t-lg"
            />
            <div className="absolute top-4 left-4 bg-white text-center p-3 rounded-lg shadow-md">
              <span className="block text-lg font-bold text-primaryBlueText">
                {new Date(eventData.start).getDate()}
              </span>
              <span className="block text-sm text-primaryBlueText">
                {new Date(eventData.start).toLocaleString("default", {
                  month: "short",
                })}
              </span>
            </div>
          </div>
        )}

        <div className="p-6">
          {/* Event Title */}
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {eventData.name}
          </h2>

          {/* Event Type Badge */}
          <div className="mb-4">
            <span className="inline-block px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full">
              {eventData.eventType}
            </span>
          </div>

          {/* Event Description */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            {eventData.description}
          </p>

          {/* Event Details */}
          <div className="space-y-4 mb-6">
            {/* Date and Time */}
            <div className="flex items-start gap-3">
              <AiOutlineCalendar
                className="text-ambassadorBlue mt-1"
                size={20}
              />
              <div>
                <p className="font-medium text-gray-800">Date</p>
                <p className="text-gray-600">{formatDate(eventData.start)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FiClock className="text-ambassadorBlue mt-1" size={20} />
              <div>
                <p className="font-medium text-gray-800">Time</p>
                <p className="text-gray-600">
                  {formatTime(eventData.start)} - {formatTime(eventData.end)}
                </p>
              </div>
            </div>

            {/* Subscriptions Count */}
            <div className="flex items-start gap-3">
              <AiOutlineUser className="text-ambassadorBlue mt-1" size={20} />
              <div>
                <p className="font-medium text-gray-800">Participants</p>
                <p className="text-gray-600">
                  {subsCount} Ambassador{" "}
                  {subsCount === 0
                    ? "participate"
                    : subsCount === 1
                    ? "participant"
                    : "participants"}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleJoiningEvent}
              className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Join
            </button>

            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailModal;
